import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { getBroker, createConnection, getMyConnections, type Broker, type Connection } from '../api/brokerApi'

export default function BrokerDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user, getAccessToken } = useAuth()
  const [broker, setBroker] = useState<Broker | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showConnect, setShowConnect] = useState(false)
  const [message, setMessage] = useState('')
  const [connecting, setConnecting] = useState(false)
  const [connected, setConnected] = useState(false)
  const [existingConnection, setExistingConnection] = useState<Connection | null>(null)

  useEffect(() => {
    if (!id) return
    getBroker(id)
      .then(setBroker)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [id])

  // Check for existing connection if authenticated
  useEffect(() => {
    const token = getAccessToken()
    if (!token || user?.userType !== 'customer') return
    getMyConnections(token)
      .then((res) => {
        const existing = res.connections.find(
          (c) => c.broker_id === id && (c.status === 'pending' || c.status === 'active'),
        )
        if (existing) setExistingConnection(existing)
      })
      .catch(() => {})
  }, [user, id])

  const handleConnect = async () => {
    const token = getAccessToken()
    if (!token || !id) return
    setConnecting(true)
    try {
      // Auto-attach last comparison snapshot if available
      let comparison_snapshot: Record<string, unknown> | undefined
      try {
        const stored = sessionStorage.getItem('last_comparison')
        if (stored) comparison_snapshot = JSON.parse(stored)
      } catch { /* ignore parse errors */ }

      await createConnection(token, { broker_id: id, message: message || undefined, comparison_snapshot })
      setConnected(true)
      setShowConnect(false)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to connect')
    } finally {
      setConnecting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading broker profile...</div>
      </div>
    )
  }

  if (error && !broker) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-500">{error}</div>
      </div>
    )
  }

  if (!broker) return null

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="text-blue-600 hover:underline"
          >
            ← Back
          </button>
          <button
            onClick={() => navigate('/customer')}
            className="text-gray-500 hover:underline"
          >
            Go to Dashboard
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-start gap-6">
            <div className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-3xl font-bold flex-shrink-0">
              {broker.photo_url ? (
                <img src={broker.photo_url} alt={broker.business_name || ''} className="w-24 h-24 rounded-full object-cover" />
              ) : (
                broker.business_name?.[0] || broker.email[0].toUpperCase()
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {broker.business_name || 'Mortgage Broker'}
              </h1>
              {broker.license_number && (
                <p className="text-sm text-gray-500">License: {broker.license_number}</p>
              )}
              {broker.years_experience && (
                <p className="text-gray-600 mt-1">{broker.years_experience} years experience</p>
              )}
              {broker.specialisation && (
                <p className="text-blue-600 mt-1">{broker.specialisation}</p>
              )}
              <span className="inline-flex items-center mt-2 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Accepting new clients
              </span>
            </div>
          </div>

          {broker.bio && (
            <div className="mt-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">About</h2>
              <p className="text-gray-600 whitespace-pre-line">{broker.bio}</p>
            </div>
          )}

          {/* Connection Status / Action */}
          <div className="mt-8 border-t pt-6">
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                {error}
              </div>
            )}

            {connected && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700">
                Connection request sent! {broker.business_name} will review your request.
              </div>
            )}

            {existingConnection && !connected && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-blue-700">
                {existingConnection.status === 'pending'
                  ? `Connection request pending — waiting for ${broker.business_name} to respond.`
                  : `You are connected with ${broker.business_name}.`}
              </div>
            )}

            {!connected && !existingConnection && (
              <>
                {user?.userType === 'customer' ? (
                  <>
                    {showConnect ? (
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold">Connect with {broker.business_name}</h3>
                        <textarea
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          placeholder="Tell the broker about your situation (optional)..."
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          rows={3}
                        />
                        <div className="flex gap-3">
                          <button
                            onClick={handleConnect}
                            disabled={connecting}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                          >
                            {connecting ? 'Sending...' : 'Send Connection Request'}
                          </button>
                          <button
                            onClick={() => setShowConnect(false)}
                            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowConnect(true)}
                        className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-lg font-semibold"
                      >
                        Connect with {broker.business_name}
                      </button>
                    )}
                  </>
                ) : (
                  <div className="text-center space-y-4">
                    <p className="text-gray-600">Sign in to connect with this broker</p>
                    <button
                      onClick={() => navigate('/login')}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Sign In to Connect
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
